import { logError } from 'lib/observability';
import { BaseDestructible } from 'utils/destructible';

export type UpdatedStateCleanupFn = () => void;
export type UpdatedStateListener<T> = (state: T, oldState: T) => UpdatedStateCleanupFn | void;

export abstract class UpdatedState<T> extends BaseDestructible {
  abstract get state(): T;
  protected abstract _subscribeWithoutCleanup(listener: UpdatedStateListener<T>): UpdatedStateCleanupFn;

  protected _subscribe(listener: UpdatedStateListener<T>, options: { triggerForCurrentState: boolean }): UpdatedStateCleanupFn {
    let callbackCleanupFn: UpdatedStateCleanupFn | undefined;
    const callCallbackCleanupFn = () => {
      callbackCleanupFn?.();
      callbackCleanupFn = undefined;
    };
    let listenerTriggered = false;
    const customListener = (newState: T, oldState: T) => {
      listenerTriggered = true;
      callCallbackCleanupFn();
      try {
        callbackCleanupFn = listener(newState, oldState) ?? undefined;
      } catch (error) {
        callbackCleanupFn = undefined;
        if (error instanceof Error) {
          logError(error);
        } else {
          logError(new Error(`Unknown error happened during listener call: ${error}`));
        }
      }
    };

    const unsubscribe = this._subscribeWithoutCleanup(customListener);

    if (options.triggerForCurrentState) {
      // We need to call this async for better ergonomics. `subscribe` get's called in constructors where we want to finis the whole constructor before we start calling effects
      Promise.resolve().then(() => {
        // Let's call it only if there wasn't earlier invocation
        if (listenerTriggered) return;
        customListener(this.state, this.state);
      });
    }

    const cleanup = () => {
      unsubscribe();
      callCallbackCleanupFn();
    };
    this.onDestroy(cleanup);

    return cleanup;
  }
  subscribe(listener: UpdatedStateListener<T>): UpdatedStateCleanupFn {
    return this._subscribe(listener, { triggerForCurrentState: false });
  }
  subscribeAndTriggerForCurrentState(listener: UpdatedStateListener<T>) {
    return this._subscribe(listener, { triggerForCurrentState: true });
  }

  /** This is can be used to limit the ability to edit value of some state */
  get readonlyUpdatedState(): UpdatedState<T> {
    return this;
  }

  /** @deprecated this should be used only for debugging purposes and it should not be enabled in prod */
  enableDebugLogging(name: string) {
    this.subscribe(state => {
      console.debug('UpdatedState', name, 'has new value:', state);
    });
  }
}
